import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { productsAPI, categoriesAPI, mediaAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Save, ArrowLeft, Trash2, Plus, X, Image as ImageIcon, Download } from 'lucide-react';
import MediaSelectorModal from '../../components/admin/MediaSelectorModal';
import { resolveImageUrl } from '../../utils/imageUrl';

const AdminProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = id && id !== 'novo';
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: '', name: '', category: '', shortDescription: '', fullDescription: '',
    applications: [], specifications: { custom: {} },
    images: [], mainImage: '', featured: false, active: true, displayOrder: 0,
    brand: '', productType: 'original'
  });
  const [newApplication, setNewApplication] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newSpecKey, setNewSpecKey] = useState('');
  const [newSpecVal, setNewSpecVal] = useState('');
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [importingUrl, setImportingUrl] = useState(false);

  useEffect(() => {
    loadCategories();
    if (id && id !== 'novo') loadProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCategories = async () => {
    try {
      const data = await categoriesAPI.list();
      setCategories(data);
    } catch (e) { console.error(e); }
  };

  const loadProduct = async () => {
    setLoading(true);
    try {
      const data = await productsAPI.get(id);
      setForm({
        code: data.code || '',
        name: data.name || '',
        category: data.category || '',
        shortDescription: data.shortDescription || '',
        fullDescription: data.fullDescription || '',
        applications: data.applications || [],
        specifications: data.specifications || { custom: {} },
        images: data.images || [],
        mainImage: data.mainImage || '',
        featured: data.featured || false,
        active: data.active !== false,
        displayOrder: data.displayOrder || 0,
        brand: data.brand || '',
        productType: data.productType || 'original'
      });
    } catch (e) {
      alert('Erro ao carregar produto');
      navigate('/dashboard/produtos');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSpecChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      specifications: { ...prev.specifications, [field]: value }
    }));
  };

  const addApplication = () => {
    if (!newApplication.trim()) return;
    setForm(prev => ({ ...prev, applications: [...prev.applications, newApplication.trim()] }));
    setNewApplication('');
  };

  const removeApplication = (index) => {
    setForm(prev => ({ ...prev, applications: prev.applications.filter((_, i) => i !== index) }));
  };

  const addImage = async () => {
    if (!newImageUrl.trim()) return;
    const url = newImageUrl.trim();
    
    // If it's an external URL (http), download it via media API
    if (url.startsWith('http://') || url.startsWith('https://')) {
      setImportingUrl(true);
      try {
        const result = await mediaAPI.importUrl(url, form.name || 'Produto', 'produtos');
        const savedUrl = resolveImageUrl(result.url);
        const updated = [...form.images, savedUrl];
        setForm(prev => ({ ...prev, images: updated, mainImage: prev.mainImage || savedUrl }));
        setNewImageUrl('');
      } catch (e) {
        console.error('Erro ao importar imagem:', e);
        alert('Erro ao importar imagem. Verifique se a URL é válida.');
      } finally {
        setImportingUrl(false);
      }
    } else {
      const resolved = resolveImageUrl(url);
      const updated = [...form.images, resolved];
      setForm(prev => ({ ...prev, images: updated, mainImage: prev.mainImage || resolved }));
      setNewImageUrl('');
    }
  };

  const removeImage = (index) => {
    const removed = form.images[index];
    const updated = form.images.filter((_, i) => i !== index);
    setForm(prev => ({
      ...prev, images: updated,
      mainImage: prev.mainImage === removed ? (updated[0] || '') : prev.mainImage
    }));
  };

  const addCustomSpec = () => {
    if (!newSpecKey.trim() || !newSpecVal.trim()) return;
    setForm(prev => ({
      ...prev,
      specifications: {
        ...prev.specifications,
        custom: { ...(prev.specifications.custom || {}), [newSpecKey.trim()]: newSpecVal.trim() }
      }
    }));
    setNewSpecKey('');
    setNewSpecVal('');
  };

  const removeCustomSpec = (key) => {
    setForm(prev => {
      const custom = { ...(prev.specifications.custom || {}) };
      delete custom[key];
      return { ...prev, specifications: { ...prev.specifications, custom } };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.name || !form.category) {
      alert('Preencha os campos obrigatórios: Código, Nome e Categoria');
      return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        await productsAPI.update(id, form);
      } else {
        await productsAPI.create(form);
      }
      navigate('/dashboard/produtos');
    } catch (error) {
      alert('Erro ao salvar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#055509]" />
        </div>
      </AdminLayout>
    );
  }

  const specFields = [
    { key: 'displacement', label: 'Deslocamento' },
    { key: 'maxPressure', label: 'Pressão Máxima' },
    { key: 'maxSpeed', label: 'Velocidade Máxima' },
    { key: 'maxTorque', label: 'Torque Máximo' },
    { key: 'fluidType', label: 'Tipo de Fluido' },
    { key: 'flow', label: 'Vazão' },
    { key: 'connection', label: 'Conexão' },
    { key: 'material', label: 'Material' },
  ];

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center gap-4 mb-6">
          <Button data-testid="back-to-products" variant="ghost" onClick={() => navigate('/dashboard/produtos')}>
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 data-testid="product-form-title" className="text-2xl font-bold text-gray-800">
              {isEditing ? 'Editar Produto' : 'Novo Produto'}
            </h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader><CardTitle>Informações Básicas</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
                  <Input data-testid="product-code-input" value={form.code} onChange={e => handleChange('code', e.target.value)} placeholder="Ex: BH-001" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <Input data-testid="product-name-input" value={form.name} onChange={e => handleChange('name', e.target.value)} placeholder="Nome do produto" required />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria *</label>
                  <select data-testid="product-category-select" value={form.category} onChange={e => handleChange('category', e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm" required>
                    <option value="">Selecione...</option>
                    {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                  <Input data-testid="product-brand-input" value={form.brand || ''} onChange={e => handleChange('brand', e.target.value)} placeholder="Ex: Rexroth, Parker, Eaton" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Produto</label>
                  <select data-testid="product-type-select" value={form.productType || 'original'} onChange={e => handleChange('productType', e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm h-10">
                    <option value="original">Produto Original</option>
                    <option value="importado">Produto Importado</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição Curta</label>
                <textarea data-testid="product-short-desc" value={form.shortDescription} onChange={e => handleChange('shortDescription', e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm min-h-[80px]" placeholder="Breve descrição do produto" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição Completa</label>
                <textarea data-testid="product-full-desc" value={form.fullDescription} onChange={e => handleChange('fullDescription', e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm min-h-[120px]" placeholder="Descrição detalhada" />
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input data-testid="product-featured-check" type="checkbox" checked={form.featured} onChange={e => handleChange('featured', e.target.checked)} className="rounded" />
                  <span className="text-sm">Produto em Destaque</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input data-testid="product-active-check" type="checkbox" checked={form.active} onChange={e => handleChange('active', e.target.checked)} className="rounded" />
                  <span className="text-sm">Produto Ativo</span>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Images */}
          <Card>
            <CardHeader><CardTitle>Imagens</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Input data-testid="product-image-url-input" value={newImageUrl} onChange={e => setNewImageUrl(e.target.value)} placeholder="Cole a URL da imagem (download autom\u00e1tico)" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addImage())} className="flex-1 min-w-[200px]" />
                <Button type="button" data-testid="add-image-btn" onClick={addImage} variant="outline" disabled={importingUrl}>
                  {importingUrl ? <><Download size={16} className="mr-1 animate-bounce" /> Baixando...</> : <><Plus size={16} className="mr-1" /> Adicionar</>}
                </Button>
                <Button type="button" data-testid="open-media-selector" onClick={() => setShowMediaModal(true)} variant="outline" className="border-[#055509] text-[#055509] hover:bg-[#055509]/5">
                  <ImageIcon size={16} className="mr-1" /> Biblioteca
                </Button>
              </div>
              {form.images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {form.images.map((img, i) => (
                    <div key={i} className={`relative group border-2 rounded-lg overflow-hidden ${form.mainImage === img ? 'border-[#055509]' : 'border-gray-200'}`}>
                      <img src={resolveImageUrl(img)} alt={`Imagem ${i + 1}`} className="w-full h-24 object-cover" />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                        <button type="button" onClick={() => handleChange('mainImage', img)} className="p-1 bg-white rounded text-xs">Principal</button>
                        <button type="button" onClick={() => removeImage(i)} className="p-1 bg-red-500 text-white rounded"><X size={12} /></button>
                      </div>
                      {form.mainImage === img && <span className="absolute top-1 left-1 bg-[#055509] text-white text-[10px] px-1 rounded">Principal</span>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <MediaSelectorModal
            isOpen={showMediaModal}
            onClose={() => setShowMediaModal(false)}
            multiple={true}
            onSelect={(urls) => {
              const newImages = Array.isArray(urls) ? urls : [urls];
              const updated = [...form.images, ...newImages];
              setForm(prev => ({ ...prev, images: updated, mainImage: prev.mainImage || updated[0] || '' }));
            }}
          />

          {/* Applications */}
          <Card>
            <CardHeader><CardTitle>Aplicações</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input data-testid="product-application-input" value={newApplication} onChange={e => setNewApplication(e.target.value)} placeholder="Ex: Tratores agrícolas" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addApplication())} />
                <Button type="button" data-testid="add-application-btn" onClick={addApplication} variant="outline"><Plus size={16} /></Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.applications.map((app, i) => (
                  <span key={i} className="bg-gray-100 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                    {app}
                    <button type="button" onClick={() => removeApplication(i)} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Specifications */}
          <Card>
            <CardHeader><CardTitle>Especificações Técnicas</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {specFields.map(sf => (
                  <div key={sf.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{sf.label}</label>
                    <Input value={form.specifications[sf.key] || ''} onChange={e => handleSpecChange(sf.key, e.target.value)} placeholder={sf.label} />
                  </div>
                ))}
              </div>
              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Especificações Personalizadas</p>
                <div className="flex gap-2 mb-3">
                  <Input value={newSpecKey} onChange={e => setNewSpecKey(e.target.value)} placeholder="Nome (ex: Peso)" className="flex-1" />
                  <Input value={newSpecVal} onChange={e => setNewSpecVal(e.target.value)} placeholder="Valor (ex: 5kg)" className="flex-1" />
                  <Button type="button" onClick={addCustomSpec} variant="outline"><Plus size={16} /></Button>
                </div>
                {form.specifications.custom && Object.entries(form.specifications.custom).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-600">{k}:</span>
                    <span className="text-sm text-gray-800">{v}</span>
                    <button type="button" onClick={() => removeCustomSpec(k)} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/dashboard/produtos')}>Cancelar</Button>
            <Button data-testid="save-product-btn" type="submit" className="bg-[#055509] hover:bg-[#0a7a0f] text-white" disabled={saving}>
              {saving ? 'Salvando...' : <><Save size={16} className="mr-2" />{isEditing ? 'Atualizar' : 'Criar Produto'}</>}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminProductForm;
